/**
 * Alerte « prix d'achat d'un COLIS lu comme un prix unitaire » — règle pure.
 *
 * Cas réel (30/09 → 04/11/2026) : la facture Vajra écrit « Qté 1,00 · Cond. 5 kg ·
 * P. Unit 11,25 · Montant 11,25 » — 11,25 € est le prix du SAC. Lu « 5 kg à
 * 11,25 €/kg », neuf lots de vrac sont entrés à 3 à 5 fois leur prix d'achat réel,
 * sans que l'écran de réception ne dise rien.
 *
 * Deux repères, dans cet ordre :
 *  1. le prix d'achat HABITUEL du produit (médiane de ses derniers lots) : un
 *     rapport proche d'un entier k ≥ 2 désigne un colis de k unités ;
 *  2. le prix de VENTE en caisse : acheter plus cher qu'on ne vend est presque
 *     toujours une erreur de lecture (sans historique, ou quand l'historique est
 *     lui-même faux).
 * Le prix reste celui de l'opérateur : l'alerte propose, elle ne corrige rien seule.
 */
export interface AlertePrixAchat {
  motif: 'habituel' | 'vente';
  /** Rapport achat / repère. */
  rapport: number;
  /** Diviseur proposé (contenu du colis) quand le rapport tombe sur un entier. */
  diviseur: number | null;
  repereCents: number;
}

const SEUIL_HABITUEL = 1.8;
const TOLERANCE_ENTIER = 0.12;

export function alertePrixAchat(p: {
  achatCents: number;
  habituelCents?: number | null;
  venteCents?: number | null;
  /** Quantité reçue : le contenu d'un colis en est souvent une fraction (7 kg = 2 × 3,5 kg). */
  quantite?: number | null;
}): AlertePrixAchat | null {
  const { achatCents } = p;
  if (!(achatCents > 0)) return null;

  const habituel = p.habituelCents ?? 0;
  if (habituel > 0) {
    const rapport = achatCents / habituel;
    if (rapport >= SEUIL_HABITUEL) {
      return { motif: 'habituel', rapport, diviseur: diviseurProbable(rapport, p.quantite), repereCents: habituel };
    }
  }

  const vente = p.venteCents ?? 0;
  if (vente > 0 && achatCents >= vente) {
    const rapport = achatCents / vente;
    return { motif: 'vente', rapport, diviseur: null, repereCents: vente };
  }
  return null;
}

/**
 * Contenu d'un colis qui explique le rapport : d'abord une fraction de la quantité
 * reçue (1 à 6 colis), car c'est elle qu'on a sous les yeux ; sinon un entier.
 * Deux sacs de 3,5 kg donnent un rapport de 3,77 : l'entier le plus proche (4)
 * serait faux, la moitié des 7 kg reçus (3,5) est juste.
 */
function diviseurProbable(rapport: number, quantite?: number | null): number | null {
  if (quantite && quantite > 0) {
    let meilleur: number | null = null;
    let ecartMin = Infinity;
    for (let n = 1; n <= 6; n++) {
      const contenu = quantite / n;
      if (contenu < 1.5) break;
      const ecart = Math.abs(rapport - contenu) / contenu;
      if (ecart <= TOLERANCE_ENTIER && ecart < ecartMin) {
        meilleur = Math.round(contenu * 1000) / 1000;
        ecartMin = ecart;
      }
    }
    if (meilleur != null) return meilleur;
  }
  const k = Math.round(rapport);
  if (k < 2 || k > 30) return null;
  return Math.abs(rapport - k) / k <= TOLERANCE_ENTIER ? k : null;
}
